'use client'

/**
 * Privacy Vault Toggle Component - Feature Flag Protected
 * Allows logged-in users to toggle between standard trading and Privacy Vault (Namada)
 *
 * FF Protection:
 * - Parent component (swap-form) checks privacyUi flag before rendering
 * - This component uses FF-protected atoms for visibility/state logic
 * - Without privacyUi flag: component doesn't render at all
 *
 * Toggle Behavior:
 * - Visible: Logged-in user, not both source + dest private
 * - Hidden: Guest user OR both source and dest are privacy vault
 * - ON: Destination is privacy vault
 * - OFF: Destination is not privacy vault
 */

import { ChainType } from '@polaris/core/models'
import { chainID } from '@polaris/core/namada'
import { useAtomValue, useSetAtom } from 'jotai'
import { Check, Shield } from 'lucide-react'
import { useQueryStates } from 'nuqs'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/src/components/ui/tooltip'
import { mpcWalletAddressesWithNamadaOverrideAtom } from '@/src/stores/mpc/atoms'
import { toSearchParamsParsers } from '@/src/stores/params'
import {
  goPrivateToggleStateAtom,
  selectedToChainAddressesAtom,
  shouldShowGoPrivateToggleAtom,
  toTokenAcrossChainsAtom,
} from '@/src/stores/swap/atoms'
import { userAtom } from '@/src/stores/user/atoms'
import { cn } from '@/src/utils/styles'

export function GoPrivateToggle() {
  // FF-protected atoms (return false/null when flag disabled)
  const shouldShow = useAtomValue(shouldShowGoPrivateToggleAtom)
  const isToggleOn = useAtomValue(goPrivateToggleStateAtom)

  // Current destination token info
  const [{ toChain, toToken }] = useQueryStates(toSearchParamsParsers)
  const toTokenData = useAtomValue(toTokenAcrossChainsAtom).data

  // Store original chain/token when toggling ON so we can restore it when toggling OFF
  const originalTokenRef = useRef<{ chain: string; token: string } | null>(null)

  // Clear originalTokenRef if user manually changes destination
  // (either to non-PV chain OR to a different PV token)
  useEffect(() => {
    if (toChain && toToken) {
      // If destination changed to non-PV, clear the ref
      if (toChain !== chainID) {
        originalTokenRef.current = null
      }
      // If destination is PV but different from what we saved, clear the ref
      // (user manually selected a different PV token via dropdown)
      else if (
        originalTokenRef.current &&
        (originalTokenRef.current.chain !== toChain ||
          originalTokenRef.current.token !== toToken)
      ) {
        originalTokenRef.current = null
      }
    }
  }, [toChain, toToken])

  // Find the Privacy Vault version from asset_across_chains
  const privacyVaultVersion = useMemo(() => {
    return toTokenData?.asset_across_chains?.find(
      (chain) => chain.chain_id === chainID,
    )
  }, [toTokenData?.asset_across_chains])

  const ticker = toTokenData?.requested_asset?.ticker

  // State and actions
  const setToParams = useQueryStates(toSearchParamsParsers)[1]
  const setSelectedToChainAddress = useSetAtom(selectedToChainAddressesAtom)
  const { data: mpcWalletAddresses } = useAtomValue(
    mpcWalletAddressesWithNamadaOverrideAtom,
  )
  const user = useAtomValue(userAtom)

  const handleToggle = useCallback(async () => {
    if (isToggleOn) {
      // Toggle OFF: Restore original chain/token OR fallback to native chain
      if (originalTokenRef.current) {
        // We have the original saved - restore it
        setToParams({
          toChain: originalTokenRef.current.chain,
          toToken: originalTokenRef.current.token,
        })
        originalTokenRef.current = null
      } else {
        // No original saved (e.g., page loaded with PV by default or manually selected PV token)
        // Strategy: Use asset_across_chains to find the native chain version

        // IMPORTANT: Verify toTokenData matches current URL params before using it
        // (toTokenData may be stale if user just changed tokens)
        const isDataStale =
          toTokenData?.requested_asset?.chain_id !== toChain ||
          toTokenData?.requested_asset?.denom !== toToken

        if (isDataStale) {
          // Data is stale (user just changed tokens, query hasn't refetched yet)
          // Wait for next render cycle when toTokenData will be updated
          console.warn(
            `[GoPrivateToggle] Token data is stale, waiting for refetch. Current: ${toChain}/${toToken}, Data: ${toTokenData?.requested_asset?.chain_id}/${toTokenData?.requested_asset?.denom}`,
          )
          return
        }

        const osmosisChainId = 'cosmos:osmosis-1'

        // First, try to find an Osmosis version in asset_across_chains
        const osmosisVersion = toTokenData?.asset_across_chains?.find(
          (chain) => chain.chain_id === osmosisChainId,
        )

        // If no Osmosis version, find any non-privacy chain
        const anyNativeVersion = toTokenData?.asset_across_chains?.find(
          (chain) => chain.chain_id !== chainID,
        )

        const nativeVersion = osmosisVersion || anyNativeVersion

        if (nativeVersion) {
          // Found a native version in asset_across_chains - use it
          setToParams({
            toChain: nativeVersion.chain_id,
            toToken: nativeVersion.denom,
          })
        } else {
          // No native version found in asset_across_chains
          // This shouldn't happen for properly configured tokens, but as a last resort:
          // Do nothing and let the user manually select a destination
          // (Better than incorrectly switching to a different token)
          console.warn(
            `[GoPrivateToggle] No native chain found for token ${ticker}. Asset may only exist on privacy chains.`,
          )
        }
      }
    } else {
      // Toggle ON: Save current token and switch to Privacy Vault
      if (toChain && toToken) {
        originalTokenRef.current = { chain: toChain, token: toToken }
      }

      // Use the Privacy Vault version found from asset_across_chains
      if (privacyVaultVersion) {
        setToParams({
          toChain: chainID,
          toToken: privacyVaultVersion.denom,
        })
        // Set the destination address if we have it
        if (mpcWalletAddresses?.namada) {
          // Namada address format: "vk:...;pay:..." - extract payment address
          const namadaAddr = mpcWalletAddresses.namada
          const paymentAddr = namadaAddr.includes(';pay:')
            ? namadaAddr.split(';pay:')[1].trim()
            : namadaAddr.trim()
          setSelectedToChainAddress([ChainType.Namada, paymentAddr])
        }
      } else {
        // Clear the saved original since we're not actually toggling
        originalTokenRef.current = null
      }
    }
  }, [
    isToggleOn,
    toChain,
    toToken,
    ticker,
    mpcWalletAddresses?.namada,
    setToParams,
    setSelectedToChainAddress,
    privacyVaultVersion,
    toTokenData?.asset_across_chains,
    toTokenData?.requested_asset?.chain_id,
    toTokenData?.requested_asset?.denom,
  ])

  // Show "account required" hint for guest users
  const showAccountRequired = user.id === 'guest'

  // Disabled when there's no private variant available (and toggle is OFF)
  const isDisabled = !isToggleOn && !privacyVaultVersion

  // ← FF GATE: Hide toggle if flag says no
  if (!shouldShow) return null

  const toggleButton = (
    <button
      type='button'
      onClick={isDisabled ? undefined : handleToggle}
      disabled={isDisabled}
      className={cn(
        'flex w-full min-h-12 pl-4 pr-2 py-2 justify-center items-center gap-3 rounded-2xl transition-colors mb-4',
        {
          'bg-green-800': isToggleOn,
          'bg-surface2': !isToggleOn,
          'opacity-50 cursor-not-allowed': isDisabled,
        },
      )}
    >
      {isToggleOn ? (
        <div className='relative w-5 h-5 shrink-0'>
          <Shield className='w-5 h-5 text-success-text' />
          <Check
            className='w-3 h-3 text-success-text absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
            strokeWidth={3}
          />
        </div>
      ) : (
        <Shield className='w-5 h-5 text-tertiary shrink-0' />
      )}
      <div className='flex flex-col flex-1 text-left gap-1'>
        <span className='text-base font-medium text-white'>Go private</span>
        {showAccountRequired && (
          <span className='text-sm text-secondary'>
            Polaris account required
          </span>
        )}
      </div>
      {/* Toggle switch */}
      <div
        className={cn(
          'relative w-11 h-6 rounded-full transition-colors shrink-0',
          {
            'bg-orange-400': isToggleOn,
            'bg-surface2': !isToggleOn,
          },
        )}
      >
        <div
          className={cn(
            'absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform',
            {
              'left-0.5': !isToggleOn,
              'left-[22px]': isToggleOn,
            },
          )}
        />
      </div>
    </button>
  )

  // Wrap in tooltip when disabled to explain why
  if (isDisabled) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>{toggleButton}</TooltipTrigger>
          <TooltipContent>
            <p className='text-sm'>
              {ticker} is not available in the Privacy Vault
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return toggleButton
}
